import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Box } from '@mui/material';
import { AuthPermissions } from 'skipify-types';
import { useMediaQuery } from '@mui/system';

import { configurationPaths, paylinkPaths } from '../../util/constants';
import { buildUrlWithMerchantId } from '../../util/helpers';
import { MerchantLogo } from './MerchantLogo';
import { useTokenRemoval } from '../../hooks';
import appTheme from '../../styles/themes/theme';
import { SubTabBar } from '../../common/SubTabBar';
import { MobileSubTabBar } from '../../common/SubTabBar/MobileSubTabBar';
import { useMerchant } from '../../contexts/MerchantContext';
import { useGetPublicMerchantQuery } from '../../store/modules/merchant/api';
import { ApplicationLoading } from '../../common';
import { useMerchantAwareNavigate } from '../../hooks/useMerchantAwareNavigate';

const TABS = [
  {
    label: 'Send Paylinks',
    id: 'tab-send-paylinks',
    path: paylinkPaths.SEND_PAYLINKS,
  },
  {
    label: 'View all Paylinks',
    id: 'tab-view-all-paylinks',
    path: paylinkPaths.VIEW_ALL,
  },
  {
    label: 'Customers',
    id: 'tab-customers',
    path: paylinkPaths.CUSTOMERS,
  },
];

export const PaylinksAppShell = () => {
  const navigate = useMerchantAwareNavigate();
  const location = useLocation();

  const dispatch = useDispatch();

  const {
    merchantId,
    merchantName: name,
    isLoading: merchantInfoLoading,
    permissions: merchantPermissions,
    canAccess,
    isChildMerchantRestrictedShell,
  } = useMerchant();
  const { data: publicData, isLoading: isLoadingPublicMerchant } = useGetPublicMerchantQuery(
    merchantId,
    { skip: !merchantId || isChildMerchantRestrictedShell },
  );
  const [selectedTab, setSelectedTab] = useState(0);
  const merchantName = publicData?.branding?.displayName ?? name;
  const isMobile = useMediaQuery(appTheme.breakpoints.down('sm'));

  // Wait for permissions to load before checking access
  const isLoadingPermissions = merchantInfoLoading || !merchantPermissions;

  // Hook-based permission checks
  const canCreatePaylinks = canAccess(AuthPermissions.CREATE_PAYLINKS);
  const canCreateOwnPaylinks = canAccess(AuthPermissions.CREATE_OWN_PAYLINKS);
  const canReadPaylinks = canAccess(AuthPermissions.READ_PAYLINKS);
  const canReadOwnPaylinks = canAccess(AuthPermissions.READ_OWN_PAYLINKS);
  const canReadPaylinkCustomers = canAccess(AuthPermissions.READ_PAYLINK_CUSTOMERS);
  const canCreatePaylinkCustomers = canAccess(AuthPermissions.CREATE_PAYLINK_CUSTOMERS);
  const canReadOwnPaylinkCustomers = canAccess(AuthPermissions.READ_OWN_PAYLINK_CUSTOMERS);

  // show only tabs that user has access to
  const PAYLINK_TABS = useMemo(() => {
    const tabs: (typeof TABS)[number][] = [];

    if (canCreatePaylinks || canCreateOwnPaylinks) {
      tabs.push(TABS[0]);
    }
    if (canReadPaylinks || canReadOwnPaylinks) {
      tabs.push(TABS[1]);
    }
    if (canReadPaylinkCustomers || canCreatePaylinkCustomers || canReadOwnPaylinkCustomers) {
      tabs.push(TABS[2]);
    }

    return tabs;
  }, [
    canCreatePaylinks,
    canCreateOwnPaylinks,
    canReadPaylinks,
    canReadOwnPaylinks,
    canReadPaylinkCustomers,
    canCreatePaylinkCustomers,
    canReadOwnPaylinkCustomers,
  ]);

  useEffect(() => {
    if (PAYLINK_TABS.length === 0) {
      return;
    }

    const matchingPathIndex = PAYLINK_TABS.findIndex(
      (tab) => tab.path.toLowerCase() === location.pathname.toLowerCase(),
    );

    // If the path is not found, navigate to the first tab
    if (matchingPathIndex === -1) {
      navigate(PAYLINK_TABS[0].path);
      setSelectedTab(0);
      return;
    }

    // If the path is found but the tab is already selected, do nothing
    if (selectedTab === matchingPathIndex) {
      return;
    }

    // If the path is found and the tab is not selected, update the selected tab
    setSelectedTab(matchingPathIndex);
  }, [location.pathname, selectedTab, PAYLINK_TABS, navigate, dispatch]);

  useTokenRemoval();

  if (isChildMerchantRestrictedShell) {
    return (
      <Navigate to={buildUrlWithMerchantId(configurationPaths.SETTINGS, merchantId)} replace />
    );
  }

  if (!merchantName || merchantInfoLoading || isLoadingPublicMerchant || isLoadingPermissions) {
    return <ApplicationLoading />;
  }

  const TabBar = isMobile ? MobileSubTabBar : SubTabBar;

  return (
    <Box
      component="main"
      data-testid="paylinks-app-shell"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        width: '100%',
        flexGrow: 1,
        pt: { xs: 0, sm: 2, md: 6 },
        px: { xs: 2, sm: 2, md: 6.5 },
        pb: 0,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: '1400px' }}>
        {!isMobile && (
          <MerchantLogo
            logoSrc={publicData?.branding?.logoSrc}
            fallbackMerchantName={merchantName}
          />
        )}
        <Box
          sx={{
            marginTop: { xs: 4, sm: 4, md: 5 },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <TabBar
            tabs={PAYLINK_TABS}
            setSelectedTab={(tabIndex, tabContent) => {
              setSelectedTab(tabIndex);
              navigate(tabContent.path);
            }}
            selectedTab={selectedTab}
            ariaLabel="Paylink Tabs"
          />
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
};
